/**
 * Has the current item that is being shown in the modifier dialog
 *  -> does validation on the item and on its modifiers
 *  -> updates the model
 *
 * Injections:
 *   cartService: for adding to cart
 *   modifierHierarchyHelper: for manipulating the item's modifiers
 */

export function CurrentItemService(cartService, cartDisplayService, cartStatusChannel, modifierHierarchyHelper, googleAnalyticsTrackingService) {
    'ngInject';
    let MAX_QUANTITY = 999,
        item = {};

    let service = {
        getItem,
        getTotalItemPrice,
        createItem,
        incrementQuantity,
        decrementQuantity,
        singleSelectOptionClicked,
        multiSelectOptionClicked,
        updateParent,
        disableOptions,
        cancelOpenedOption,
        incrementOptionQuantity,
        decrementOptionQuantity,
        modifierGroupsSatisfied,
        addItemToCart
    };

    return service;

    /**
     * getTotalItemPrice(): returns the total item pricing including selected options and quantity
     */
    function getTotalItemPrice() {
        var i,
            priceIncrease = 0;

        if (item.modifierGroups) {
            for (i = 0; i < item.modifierGroups.length; i++) {
                priceIncrease += modifierHierarchyHelper.getTotalOptionsPrice(item.modifierGroups[i]);
            }
        }

        return (item.price + priceIncrease) * item.quantity;
    }

    function getItem() {
        return item;
    }

    /**
     * createItem(): creates the current item based on the given itemReference
     *  -> WARNING: this creates a NEW object reference for item
     *
     * @param group
     * @param itemReference
     */
    function createItem(group, itemReference) {
        var i;

        /**
         * Stores the item that is currently being shown.
         * Null means the dialog is hidden.
         *
         * When not null, this will be an object with the following fields:
         *   name : for display
         *   description : for display
         *   price : for display, can change
         *   inventoryCount : for front end validation
         *   groupIdString : for cart validation & placing an order
         *   idString : for cart validation & placing an order
         *   uniqueIdString : id for an item on a cart. Only used when editing an item.
         *   quantity : for display, can change
         *   specialRequest : for placing an order
         *   modifierGroups : for displaying the item's modifiers
         *   requiredGroups : array of modifierGroups that are required; for left pane display
         *   optionalGroups : array of modifierGroups that are optional; for left pane display
         */
        item = {
            guid: itemReference.guid,
            name: itemReference.name,
            description: itemReference.description,
            // If size pricing, price will not be set. Initialize it as 0 if so.
            price: itemReference.price || 0,
            inventoryCount: itemReference.inventoryCount,
            groupIdString: group.idString,
            groupGuid: group.guid,
            idString: itemReference.idString,
            quantity: itemReference.quantity || 1,
            specialRequest: itemReference.specialRequest,
            requiredGroups: [],
            optionalGroups: []
        };

        // This will be used to identify an already existing item when adding to cart
        if (itemReference.hasOwnProperty('uniqueIdString')) {
            item.uniqueIdString = itemReference.uniqueIdString;
        }

        // Split the modifier groups into required and optional (assuming modifierGroups is sorted)
        for (i = 0; i < itemReference.modifierGroups.length; i++) {
            if (itemReference.modifierGroups[i].minSelections > 0) {
                item.requiredGroups.push(itemReference.modifierGroups[i]);
            } else {
                item.optionalGroups.push(itemReference.modifierGroups[i]);
            }
        }

        // Set modifierGroups
        item.modifierGroups = itemReference.modifierGroups;
    }

    /**
     * decrementQuantity(): decreases the number of the item to be added, minimum 1
     */
    function decrementQuantity() {
        if (item.quantity && item.quantity > 1) {
            item.quantity -= 1;
            if (item.inventoryCount !== undefined) {
                item.inventoryCount += 1;
            }
        }
    }

    /**
     * incrementQuantity(): increases the number of the item to be added
     */
    function incrementQuantity() {
        var hasInventoryCount = item.inventoryCount !== undefined;

        if (item.quantity && item.quantity < MAX_QUANTITY && (item.inventoryCount > 0 || !hasInventoryCount)) {
            item.quantity += 1;
            if (hasInventoryCount) {
                item.inventoryCount -= 1;
            }
        }
    }

    /**
     * singleSelectOptionClicked(): handles when a radio option is clicked on
     *   -> Note: this function is called AFTER the selected flag on the option is changed by the checkbox
     *
     * @param parentGroup: parent modifierGroup of the given option
     * @param option: the option corresponding to the radio button that was just clicked.
     */
    function singleSelectOptionClicked(parentGroup, option) {
        var i;

        // You can always open or close an option that has children
        if (option.modifierGroups.length > 0) {
            option.opened = !option.opened;
            option.selected = !option.selected;
        }

        if (option.opened || option.selected) {
            // Opening or selecting an option deselects and closes all other options with siblings
            for (i = 0; i < parentGroup.options.length; i++) {
                if (parentGroup.options[i].idString !== option.idString) {
                    parentGroup.options[i].selected = false;

                    if (parentGroup.options[i].modifierGroups.length > 0) {
                        parentGroup.options[i].opened = false;
                    }
                }

            }
        }

        // Updates the modifierGroups to account for the option being selected or deselected
        for (i = 0; i < item.modifierGroups.length; i++) {
            modifierHierarchyHelper.updateModifierHierarchy(item.modifierGroups[i]);
        }

        if (option.hasOwnProperty('quantity')) {
            option.quantity = option.selected ? 1 : 0;
        }

        if (option.modifierGroups.length === 0) {
            googleAnalyticsTrackingService.trackModifierSelection(option.selected);
        }
    }

    /**
     * multiSelectOptionClicked(): handles when a multi-select option is clicked on
     *   -> Note: this function is called AFTER the selected flag on the option is changed by the checkbox
     *
     * @param parentGroup: parent modifierGroup of the given option
     * @param option: the option corresponding to the checkbox that was just clicked.
     */
    function multiSelectOptionClicked(parentGroup, option) {

        var i;

        if (option.modifierGroups.length === 0) {
            // If deselecting an option will allow for more selections, enable all in stock options
            if (!option.selected && parentGroup.maxSelectionsMet) {
                for (i = 0; i < parentGroup.options.length; i++) {
                    parentGroup.options[i].disabled = parentGroup.options[i].inventoryCount === 0;
                }
            }

            // Updates the modifierGroups to account for the option being selected or deselected
            for (i = 0; i < item.modifierGroups.length; i++) {
                modifierHierarchyHelper.updateModifierHierarchy(item.modifierGroups[i]);
            }
        } else {
            // If we are clicking an option with children, its selected flag is not only dictated by whether it's
            // open but also based on whether its children modifier groups are satisfied
            option.opened = !option.opened;
            option.selected = !option.selected;

            if (option.opened) {
                for (i = 0; i < item.modifierGroups.length; i++) {
                    modifierHierarchyHelper.updateModifierHierarchy(item.modifierGroups[i]);
                }
            }
        }

        // If max selections were met after selection, disable the other options
        if (option.selected && parentGroup.maxSelectionsMet) {
            disableOptions(parentGroup.options);
        }

        if (option.hasOwnProperty('quantity')) {
            option.quantity = option.selected ? 1 : 0;
        }

        if (option.modifierGroups.length === 0) {
            googleAnalyticsTrackingService.trackModifierSelection(option.selected);
        }
    }

    /**
     * disableOptions(): iterates over options and sets them to disabled. Also closes options
     * with nested modifiers if they are not selected.
     *
     * @param options: the option corresponding to the parentGroup that was just clicked.
     */
    function disableOptions(options) {
        var i;

        for (i = 0; i < options.length; i++) {
            if (options[i].hasOwnProperty('modifierGroups') && options[i].modifierGroups.length !== 0 &&
                    !options[i].selected) {
                options[i].opened = false;
            }
            options[i].disabled = !options[i].selected;
        }
    }

    /**
     * updateParent(): Handles updating parent group when state of nested modifiers
     * is modified
     *
     * @param  parentGroup: parent modifierGroup of the given option
     * @param  option: the option corresponding to the checkbox that was just clicked.
     */
    function updateParent(parentGroup, option) {
        if (option.selected && parentGroup.maxSelectionsMet && parentGroup.maxSelections !== 1) {
            // disable options if not single select and max options selected
            disableOptions(parentGroup.options);
        } else if (!option.selected) {
            // If deselecting an option will allow for more selections, enable all in stock options
            for (var i = 0; i < parentGroup.options.length; i++) {
                parentGroup.options[i].disabled = parentGroup.options[i].inventoryCount === 0;
            }

            // Updates the modifierGroups to account for the option being selected or deselected
            for (i = 0; i < item.modifierGroups.length; i++) {
                modifierHierarchyHelper.updateModifierHierarchy(item.modifierGroups[i]);
            }
        }
    }

    /**
     * cancelOpenedOption(): used for open multi-select options. Will clear the option's children and close it
     */
    function cancelOpenedOption(parentGroup, option) {
        var i;

        // Reset to defaults
        modifierHierarchyHelper.initializeModifierHierarchy(option);

        // We close and deselect the option no matter what, even if it was opened because it's a default option
        option.opened = false;
        option.selected = false;

        // Update flags
        for (i = 0; i < item.modifierGroups.length; i++) {
            modifierHierarchyHelper.updateModifierHierarchy(item.modifierGroups[i]);
        }

        // Since this deselects the option, need to check whether there is capacity to select options again
        if (!parentGroup.maxSelectionsMet) {
            for (i = 0; i < parentGroup.options.length; i++) {
                parentGroup.options[i].disabled = parentGroup.options[i].inventoryCount === 0;
            }
        }

        googleAnalyticsTrackingService.trackModifierSelection(option.selected);
    }

    /**
     * incrementOptionQuantity(): for duplicate modifiers
     *  -> Updates the quantity on the option
     *
     * @param parentGroup: for checking if maxSelections is satisfied
     * @param option: the option that the user wants to change the quantity on
     */
    function incrementOptionQuantity(parentGroup, option) {
        var i,
            hasInventoryCount = option.inventoryCount !== undefined;

        if (option.quantity < MAX_QUANTITY && (option.inventoryCount > 0 || !hasInventoryCount) && !parentGroup.maxSelectionsMet) {
            option.quantity += 1;
            option.selected = true;

            if (hasInventoryCount) {
                option.inventoryCount -= 1;
            }
        } else {
            return;
        }

        for (i = 0; i < item.modifierGroups.length; i++) {
            modifierHierarchyHelper.updateModifierHierarchy(item.modifierGroups[i]);
        }

        // Update multiSelect if max selections are met
        if (parentGroup.maxSelectionsMet) {
            for (i = 0; i < parentGroup.options.length; i++) {
                parentGroup.options[i].disabled = !parentGroup.options[i].selected;
            }
        }
    }

    function decrementOptionQuantity(parentGroup, option) {
        var i;

        if (option.quantity >= 1) {
            option.quantity -= 1;

            if (option.inventoryCount !== undefined) {
                option.inventoryCount += 1;
            }

            // Return option to a fully deselected state
            if (option.quantity === 0) {
                option.selected = false;
                if (option.modifierGroups.length > 0) {
                    cancelOpenedOption(parentGroup, option);
                }
            }
        } else {
            return;
        }

        for (i = 0; i < item.modifierGroups.length; i++) {
            modifierHierarchyHelper.updateModifierHierarchy(item.modifierGroups[i]);
        }

        // Update multiSelect if deselecting frees up space to select other options
        if (!parentGroup.maxSelectionsMet) {
            for (i = 0; i < parentGroup.options.length; i++) {
                parentGroup.options[i].disabled = parentGroup.options[i].inventoryCount === 0;
            }
        }
    }

    /**
     * modifierGroupsSatisfied(): Checks if all the top level groups have their minimum selections satisfied
     *  -> ignores special request
     * @returns {boolean}
     */
    function modifierGroupsSatisfied() {
        var i;

        // Check if item is even initialized
        if (!item || !item.modifierGroups) {
            return false;
        }

        for (i = 0; i < item.modifierGroups.length; i++) {
            if (!item.modifierGroups[i].minSatisfied && !item.modifierGroups[i].isSpecialRequest) {
                return false;
            }
        }
        return true;
    }

    /**
     * addItemToCart(): add item to cart service. Update the price on the item so that it's the total price
     *                  including options
     */
    function addItemToCart() {
        item.price = getTotalItemPrice();

        googleAnalyticsTrackingService.trackAddOrUpdateItemEvent(item);

        return cartService.addCartItem(item)
            .then(function(response) {
                // Once item is added to cart, reset the current item
                item = {};
                cartDisplayService.updateFulfillmentTimeFromCart();

                cartStatusChannel.changedCart();
                return response;
            });
    }

}
